import axiosInstance from './axiosInstance';

interface NotificationFilters {
  userId?: string;
  type?: 'task-created' | 'task-approved' | 'task-rejected' | 'task-assigned' | 'task-overdue' | 'task-completed' |
        'user-created' | 'user-updated' | 'user-deleted' | 'user-status-changed' |
        'role-created' | 'role-updated' | 'role-deleted' |
        'project-created' | 'project-updated' | 'project-deleted' |
        'equipment-created' | 'equipment-updated' | 'equipment-deleted' |
        'system' | 'security-alert' | 'maintenance-required';
  read?: boolean;
  priority?: 'low' | 'medium' | 'high' | 'critical';
  actionRequired?: boolean;
  taskId?: string;
  sortBy?: string;
  limit?: number;
  page?: number;
}

interface CreateNotificationData {
  userId: string;
  type: 'task-created' | 'task-approved' | 'task-rejected' | 'task-assigned' | 'task-overdue' | 'task-completed' |
        'user-created' | 'user-updated' | 'user-deleted' | 'user-status-changed' |
        'role-created' | 'role-updated' | 'role-deleted' |
        'project-created' | 'project-updated' | 'project-deleted' |
        'equipment-created' | 'equipment-updated' | 'equipment-deleted' |
        'system' | 'security-alert' | 'maintenance-required';
  title: string;
  message: string;
  priority?: 'low' | 'medium' | 'high' | 'critical';
  actionRequired?: boolean;
  actionUrl?: string;
  taskId?: string;
  relatedEntityId?: string;
  relatedEntityType?: string;
  expiresAt?: Date;
}

interface UpdateNotificationData {
  read?: boolean;
  title?: string;
  message?: string;
  actionUrl?: string;
}

interface NotificationServiceResponse<T = any> {
  success: boolean;
  message: string;
  data: T;
}

class NotificationService {
  private baseUrl = '/notifications';

  // Basic CRUD operations
  async getNotifications(filters: NotificationFilters = {}): Promise<NotificationServiceResponse> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, String(value));
      }
    });

    const response = await axiosInstance.get(`${this.baseUrl}?${params.toString()}`);
    return response.data;
  }

  async getMyNotifications(filters: Omit<NotificationFilters, 'userId'> = {}): Promise<NotificationServiceResponse> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, String(value));
      }
    });

    const response = await axiosInstance.get(`${this.baseUrl}/my?${params.toString()}`);
    return response.data;
  }

  async getNotificationById(notificationId: string): Promise<NotificationServiceResponse> {
    const response = await axiosInstance.get(`${this.baseUrl}/${notificationId}`);
    return response.data;
  }

  async createNotification(notificationData: CreateNotificationData): Promise<NotificationServiceResponse> {
    const response = await axiosInstance.post(this.baseUrl, notificationData);
    return response.data;
  }

  async updateNotification(notificationId: string, updateData: UpdateNotificationData): Promise<NotificationServiceResponse> {
    const response = await axiosInstance.patch(`${this.baseUrl}/${notificationId}`, updateData);
    return response.data;
  }

  async deleteNotification(notificationId: string): Promise<NotificationServiceResponse> {
    const response = await axiosInstance.delete(`${this.baseUrl}/${notificationId}`);
    return response.data;
  }

  // Read status operations
  async markAsRead(notificationId: string): Promise<NotificationServiceResponse> {
    const response = await axiosInstance.patch(`${this.baseUrl}/${notificationId}/mark-read`);
    return response.data;
  }

  async markAllAsRead(): Promise<NotificationServiceResponse> {
    const response = await axiosInstance.patch(`${this.baseUrl}/my/mark-all-read`);
    return response.data;
  }

  async getUnreadCount(): Promise<NotificationServiceResponse<{ count: number }>> {
    const response = await axiosInstance.get(`${this.baseUrl}/my/unread-count`);
    return response.data;
  }

  // Helper methods for specific notification types
  async getTaskNotifications(taskId: string, filters: Omit<NotificationFilters, 'taskId'> = {}): Promise<NotificationServiceResponse> {
    return this.getMyNotifications({ ...filters, taskId });
  }

  async getUnreadNotifications(filters: Omit<NotificationFilters, 'read'> = {}): Promise<NotificationServiceResponse> {
    return this.getMyNotifications({ ...filters, read: false });
  }

  async getActionRequiredNotifications(filters: Omit<NotificationFilters, 'actionRequired'> = {}): Promise<NotificationServiceResponse> {
    return this.getMyNotifications({ ...filters, actionRequired: true });
  }

  async getNotificationsByType(
    type: 'task-created' | 'task-approved' | 'task-rejected' | 'task-assigned' | 'task-overdue' | 'task-completed' | 'system',
    filters: Omit<NotificationFilters, 'type'> = {}
  ): Promise<NotificationServiceResponse> {
    return this.getMyNotifications({ ...filters, type });
  }

  async getNotificationsByPriority(
    priority: 'low' | 'medium' | 'high' | 'critical',
    filters: Omit<NotificationFilters, 'priority'> = {}
  ): Promise<NotificationServiceResponse> {
    return this.getMyNotifications({ ...filters, priority });
  }

  // Get recent notifications for dropdown (last 10)
  async getRecentNotifications(): Promise<NotificationServiceResponse> {
    return this.getMyNotifications({
      limit: 10,
      sortBy: 'createdAt:desc'
    });
  }

  // Bulk operations
  async markMultipleAsRead(notificationIds: string[]): Promise<NotificationServiceResponse> {
    const promises = notificationIds.map(id => this.markAsRead(id));
    const results = await Promise.allSettled(promises);
    
    const successful = results.filter(result => result.status === 'fulfilled').length;
    const failed = results.filter(result => result.status === 'rejected').length;

    return {
      success: failed === 0,
      message: `${successful} notifications marked as read${failed > 0 ? `, ${failed} failed` : ''}`,
      data: { successful, failed }
    };
  }

  async deleteMultipleNotifications(notificationIds: string[]): Promise<NotificationServiceResponse> {
    const promises = notificationIds.map(id => this.deleteNotification(id));
    const results = await Promise.allSettled(promises);
    
    const successful = results.filter(result => result.status === 'fulfilled').length;
    const failed = results.filter(result => result.status === 'rejected').length;

    return {
      success: failed === 0,
      message: `${successful} notifications deleted${failed > 0 ? `, ${failed} failed` : ''}`,
      data: { successful, failed }
    };
  }

  // Real-time notification polling (for basic real-time updates)
  async pollForNewNotifications(lastCheckTime?: Date): Promise<NotificationServiceResponse> {
    const filters: NotificationFilters = {
      read: false,
      sortBy: 'createdAt:desc',
      limit: 50
    };

    if (lastCheckTime) {
      // This would require backend support for filtering by creation time
      // For now, we'll just get all unread notifications
    }

    return this.getUnreadNotifications(filters);
  }

  // Utility methods
  getNotificationTypeLabel(type: string): string {
    const labels: Record<string, string> = {
      'task-created': 'Task Created',
      'task-approved': 'Task Approved',
      'task-rejected': 'Task Rejected',
      'task-assigned': 'Task Assigned',
      'task-overdue': 'Task Overdue',
      'task-completed': 'Task Completed',
      'user-created': 'User Created',
      'user-updated': 'User Updated',
      'user-deleted': 'User Deleted',
      'user-status-changed': 'User Status Changed',
      'role-created': 'Role Created',
      'role-updated': 'Role Updated',
      'role-deleted': 'Role Deleted',
      'project-created': 'Project Created',
      'project-updated': 'Project Updated',
      'project-deleted': 'Project Deleted',
      'equipment-created': 'Equipment Created',
      'equipment-updated': 'Equipment Updated',
      'equipment-deleted': 'Equipment Deleted',
      'system': 'System Notification',
      'security-alert': 'Security Alert',
      'maintenance-required': 'Maintenance Required'
    };
    return labels[type] || type;
  }

  getPriorityColor(priority: string): string {
    const colors: Record<string, string> = {
      'low': 'text-green-600 bg-green-50',
      'medium': 'text-yellow-600 bg-yellow-50',
      'high': 'text-orange-600 bg-orange-50',
      'critical': 'text-red-600 bg-red-50'
    };
    return colors[priority] || 'text-gray-600 bg-gray-50';
  }

  getPriorityIcon(priority: string): string {
    const icons: Record<string, string> = {
      'low': '🟢',
      'medium': '🟡',
      'high': '🟠',
      'critical': '🔴'
    };
    return icons[priority] || '⚪';
  }
}

export default new NotificationService();